import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'
import { withSessionRecovery } from '@/services/session-recovery'
import type { ApiResponse, PaginatedResponse } from '@/types'

const DEFAULT_COUNTRY_ID = 94

export interface OrgUserEmail {
  email?: string
}

export interface OrgUserPhone {
  mobile_number?: string
  phone_number?: string
  optional_mobile_number?: string
  fstsegmnt?: string
}

export interface OrgUserAddress {
  id: string | number
  user_id?: string | number | null
  name?: string | null
  email?: string | null
  mobile_number?: string | null
  optional_mobile_number?: string | null
  building_name?: string | null
  floor_line?: string | null
  street_line?: string | null
  zipcode?: string | null
  door_no?: string | null
  city?: string | number | null
  city_name?: string | null
  state?: string | number | null
  state_name?: string | null
  country?: string | number | null
  country_name?: string | null
  latitude?: string | null
  longitude?: string | null
  status?: number | null
  status_name?: string | null
  type?: number | null
  type_name?: string | null
  default?: boolean
  access_instructions?: string | null
  location_pin?: string | null
  entry_code?: string | null
  opening_time?: string | null
  closing_time?: string | null
  saturday_closed?: boolean
  sunday_closed?: boolean
}

export interface OrgUser {
  id: string
  user_id: number
  created_on?: string | null
  is_active?: boolean
  is_locked?: boolean
  can_delete?: boolean
  linked_expense_count?: number
  linked_job_count?: number
  title?: string | null
  fname?: string | null
  mname?: string | null
  lname?: string | null
  username?: string | null
  email_data?: OrgUserEmail[]
  phonenumber_data?: OrgUserPhone[]
  status?: string | null
  registration_status?: number | null
  deleted?: boolean
  email?: string | null
  phone?: string | null
  user_group?: string | number | null
  user_group_title?: string | null
  user_group_code?: string | null
  usr_reg_num?: string | null
  open_booking_count?: number
  completed_booking_count?: number
  open_job_count?: number
  completed_job_count?: number
  last_booking_at?: string | null
  last_job_at?: string | null
  total_unpaid_booking_amount?: string | number | null
  credit_outstanding_balance?: string | number | null
  total_credit_so_far?: string | number | null
  address_data?: OrgUserAddress[] | OrgUserAddress | null
  addresses?: OrgUserAddress[] | null
}

export interface OrgUserGroup {
  id: string | number
  title?: string | null
  unique_code?: string | null
  status?: string | null
  status_id?: number | null
  org_instance_id?: number | null
  org_instance_external_id?: string | null
  is_system?: boolean
  user_nativity?: string | null
  show_in_sidebar?: boolean
}

type OrgUserGroupPayload = {
  id?: string | number
  title?: string | null
  unique_code?: string | null
  status?: string | null
  status_id?: number | null
  org_instance_id?: number | null
  org_instance_external_id?: string | null
  is_system?: boolean
  user_nativity?: string | null
  show_in_sidebar?: boolean
}

type OrgUserGroupUpsertPayload = {
  title: string
  serviceInstanceId: string | number
  serviceVariant: string
}

export interface SupplierPayload {
  fname: string
  lname?: string
  title?: string
  website?: string
  email?: string
  phone?: string
  country_id?: number
  user_group?: string | number
  is_active?: boolean
}

export interface ClientPayload {
  fname: string
  lname?: string
  title?: string
  email?: string
  phone?: string
  country_id?: number
  user_group?: string | number
  is_active?: boolean
}

export interface OrgUserAddressPayload {
  name?: string
  email?: string
  mobile_number?: string
  optional_mobile_number?: string
  type?: number
  door_no?: string
  building_name?: string
  floor_line?: string
  street_line?: string
  zipcode?: string
  city_name?: string
  state?: string | number
  country?: string | number
  latitude?: string
  longitude?: string
  status?: number
  access_instructions?: string
  location_pin?: string
  entry_code?: string
  opening_time?: string
  closing_time?: string
  saturday_closed?: boolean
  sunday_closed?: boolean
}

type SupplierListOptions = {
  search?: string
  includeInactive?: boolean
  serviceInstanceId?: string | null
}

type OrgUserListPayload =
  | OrgUser[]
  | PaginatedResponse<OrgUser>
  | {
      data?: OrgUser[]
      results?: OrgUser[]
      count?: number
      recordsTotal?: number
      recordsFiltered?: number
    }

const getListPayload = <T>(payload: unknown): T[] => {
  if (Array.isArray(payload)) {
    return payload as T[]
  }

  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>
    if (Array.isArray(record.data)) {
      return record.data as T[]
    }
    if (Array.isArray(record.results)) {
      return record.results as T[]
    }
    if (record.data && typeof record.data === 'object' && Array.isArray((record.data as Record<string, unknown>).results)) {
      return (record.data as Record<string, unknown>).results as T[]
    }
  }

  return []
}

const normalizeOrgUsers = (payload?: OrgUserListPayload): OrgUser[] => {
  if (!payload) {
    return []
  }

  if (Array.isArray(payload)) {
    return payload
  }

  if (Array.isArray(payload.results)) {
    return payload.results
  }

  if ('data' in payload && Array.isArray(payload.data)) {
    return payload.data
  }

  return []
}

const normalizeUserGroups = <T>(payload?: ApiResponse<PaginatedResponse<T>> | PaginatedResponse<T> | T[]): T[] => {
  if (!payload) {
    return []
  }

  const data = 'data' in payload ? payload.data : payload

  if (Array.isArray(data)) {
    return data
  }

  if (Array.isArray(data.results)) {
    return data.results as T[]
  }

  return []
}

const mapUserGroup = (group: OrgUserGroupPayload): OrgUserGroup => ({
  id: String(group.id ?? ''),
  title: group.title ?? null,
  unique_code: group.unique_code ?? null,
  status: group.status ?? null,
  status_id: group.status_id ?? null,
  org_instance_id: group.org_instance_id ?? null,
  org_instance_external_id: group.org_instance_external_id ?? null,
  is_system: Boolean(group.is_system),
  user_nativity: group.user_nativity ?? 'home',
  show_in_sidebar: Boolean(group.show_in_sidebar),
})

const unwrapApiData = <T>(payload: ApiResponse<T> | T): T => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as ApiResponse<T>).data
  }
  return payload as T
}

const dedupeUsers = (users: OrgUser[]) => {
  const seen = new Set<string>()
  return users.filter((user) => {
    const key = String(user.user_id ?? user.id)
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
}

const nestedText = (source: unknown, field: string) => {
  if (!source || typeof source !== 'object') {
    return null
  }

  const value = (source as Record<string, unknown>)[field]
  const text = String(value ?? '').trim()
  return text || null
}

const mapAddress = (address: Partial<OrgUserAddress> | null | undefined): OrgUserAddress => ({
  id: String(address?.id ?? ''),
  user_id: address?.user_id ?? null,
  name: address?.name ?? null,
  email: address?.email ?? null,
  mobile_number: address?.mobile_number ?? null,
  optional_mobile_number: address?.optional_mobile_number ?? null,
  building_name: address?.building_name ?? null,
  floor_line: address?.floor_line ?? null,
  street_line: address?.street_line ?? null,
  zipcode: address?.zipcode ?? null,
  door_no: address?.door_no ?? null,
  city: address?.city ?? null,
  city_name: address?.city_name ?? nestedText((address as Record<string, unknown> | undefined)?.city__data, 'city_name'),
  state: address?.state ?? null,
  state_name: address?.state_name ?? nestedText((address as Record<string, unknown> | undefined)?.state__data, 'state_name'),
  country: address?.country ?? null,
  country_name: address?.country_name ?? nestedText((address as Record<string, unknown> | undefined)?.country__data, 'country_name'),
  latitude: address?.latitude ?? null,
  longitude: address?.longitude ?? null,
  status: address?.status ?? null,
  status_name: address?.status_name ?? null,
  type: address?.type ?? null,
  type_name: address?.type_name ?? null,
  default: Boolean(address?.default),
  access_instructions: address?.access_instructions ?? null,
  location_pin: address?.location_pin ?? null,
  entry_code: address?.entry_code ?? null,
  opening_time: address?.opening_time ?? null,
  closing_time: address?.closing_time ?? null,
  saturday_closed: Boolean(address?.saturday_closed),
  sunday_closed: Boolean(address?.sunday_closed),
})

const normalizeAddressList = (payload?: unknown): OrgUserAddress[] => {
  const rows = getListPayload<OrgUserAddress>(payload)
  return rows.map(mapAddress)
}

const normalizeUserAddressList = (user: Pick<OrgUser, 'address_data' | 'addresses'>): OrgUserAddress[] => {
  if (Array.isArray(user.addresses) && user.addresses.length > 0) {
    return user.addresses.map(mapAddress)
  }

  if (Array.isArray(user.address_data)) {
    return user.address_data.map(mapAddress)
  }

  if (user.address_data && typeof user.address_data === 'object') {
    return [mapAddress(user.address_data)]
  }

  return []
}

const mapSupplier = (supplier: OrgUser): OrgUser => ({
  ...supplier,
  is_active:
    supplier.is_active
    ?? !(
      String(supplier.status) === 'Inactive'
      || String(supplier.status) === '0'
    ),
  email_data: supplier.email_data ?? (supplier.email ? [{ email: supplier.email }] : []),
  phonenumber_data: supplier.phonenumber_data ?? (supplier.phone ? [{ mobile_number: supplier.phone }] : []),
  address_data: normalizeUserAddressList(supplier),
})

const mapClient = (client: OrgUser): OrgUser => ({
  ...client,
  is_active:
    client.is_active
    ?? !(
      String(client.status) === 'Inactive'
      || String(client.status) === '0'
    ),
  email_data: client.email_data ?? (client.email ? [{ email: client.email }] : []),
  phonenumber_data: client.phonenumber_data ?? (client.phone ? [{ mobile_number: client.phone }] : []),
  address_data: normalizeUserAddressList(client),
})

type OrgUserListOptions = {
  search?: string
  page?: number
  pageSize?: number
  userGroupId?: string | number
  userGroupCode?: string | null
  userGroupUnique?: string | null
  orgUser?: boolean
  includeInactive?: boolean
  serviceInstanceId?: string | number | null
  includeCustomerActivity?: boolean
}

export type OrgUserListPage = {
  results: OrgUser[]
  total: number
  page: number
  page_size: number
  unpaid_totals?: {
    total_unpaid_booking_amount?: string | number | null
    customers_with_unpaid?: number | null
  }
}

const getFirstEmail = (user: OrgUser) => user.email ?? user.email_data?.find((entry) => entry.email)?.email ?? null
const getFirstPhone = (user: OrgUser) => user.phone ?? user.phonenumber_data?.find((entry) => entry.mobile_number || entry.phone_number || entry.fstsegmnt)?.mobile_number ?? user.phonenumber_data?.find((entry) => entry.phone_number || entry.fstsegmnt)?.phone_number ?? user.phonenumber_data?.find((entry) => entry.fstsegmnt)?.fstsegmnt ?? null

const mapUser = (user: OrgUser): OrgUser => ({
  ...user,
  email: getFirstEmail(user),
  phone: getFirstPhone(user),
  address_data: normalizeUserAddressList(user),
  is_active:
    user.is_active
    ?? !(
      String(user.status).toLowerCase() === 'inactive'
      || String(user.status) === '0'
    ),
})

export const orgUsersApi = {
  list: async (options: string | OrgUserListOptions = {}): Promise<OrgUser[]> => {
    const normalizedOptions = typeof options === 'string' ? { search: options } : options
    const search = String(normalizedOptions.search ?? '').trim()
    const page = normalizedOptions.page && normalizedOptions.page > 0 ? normalizedOptions.page : 1
    const pageSize = normalizedOptions.pageSize && normalizedOptions.pageSize > 0 ? normalizedOptions.pageSize : 20
    const start = (page - 1) * pageSize
    const { userGroupId, userGroupCode, userGroupUnique, orgUser = false, includeInactive = false, serviceInstanceId } = normalizedOptions

    return withSessionRecovery(async () => {
      const response = await api.get<ApiResponse<OrgUserListPayload> | OrgUserListPayload>(API_PATHS.orgUsers.list, {
        params: {
          search,
          page,
          page_size: pageSize,
          length: pageSize,
          start,
          draw: 1,
          user_group_id: userGroupId,
          user_group_unique: userGroupUnique || userGroupCode || undefined,
          user_group_code: userGroupCode || undefined,
          org_user: orgUser ? 1 : undefined,
          include_inactive: includeInactive ? 1 : undefined,
          service_instance_id: serviceInstanceId || undefined,
        },
      })

      const payload = 'data' in response.data ? response.data.data : response.data
      return normalizeOrgUsers(payload).map(mapUser)
    })
  },

  listPaginated: async (options: OrgUserListOptions = {}): Promise<OrgUserListPage> => {
    const search = String(options.search ?? '').trim()
    const page = options.page && options.page > 0 ? options.page : 1
    const pageSize = options.pageSize && options.pageSize > 0 ? options.pageSize : 20
    const start = (page - 1) * pageSize
    const { userGroupId, userGroupCode, userGroupUnique, orgUser = false, includeInactive = false, serviceInstanceId, includeCustomerActivity = false } = options

    return withSessionRecovery(async () => {
      const response = await api.get<ApiResponse<OrgUserListPayload> | OrgUserListPayload>(API_PATHS.orgUsers.list, {
        params: {
          search,
          page,
          page_size: pageSize,
          length: pageSize,
          start,
          draw: 1,
          user_group_id: userGroupId,
          user_group_unique: userGroupUnique || userGroupCode || undefined,
          user_group_code: userGroupCode || undefined,
          org_user: orgUser ? 1 : undefined,
          include_inactive: includeInactive ? 1 : undefined,
          service_instance_id: serviceInstanceId || undefined,
          include_customer_activity: includeCustomerActivity ? 1 : undefined,
        },
      })

      const envelope = response.data as Record<string, unknown>
      const payload = ('data' in envelope && envelope.data && typeof envelope.data === 'object')
        ? envelope.data
        : envelope
      const results = normalizeOrgUsers(payload as OrgUserListPayload).map(mapUser)

      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        const record = payload as Record<string, unknown>
        const total = Number(record.total ?? record.count ?? record.recordsFiltered ?? results.length)
        const unpaidTotals = record.unpaid_totals ?? envelope.unpaid_totals
        return {
          results,
          total: Number.isFinite(total) ? total : results.length,
          page: Number(record.page ?? page),
          page_size: Number(record.page_size ?? pageSize),
          unpaid_totals: unpaidTotals && typeof unpaidTotals === 'object'
            ? unpaidTotals as OrgUserListPage['unpaid_totals']
            : undefined,
        }
      }

      return {
        results,
        total: results.length,
        page,
        page_size: pageSize,
      }
    })
  },

  listGroups: async (options: { serviceInstanceId?: string | number | null; serviceVariant?: string | null } = {}): Promise<OrgUserGroup[]> => {
    const response = await api.get<ApiResponse<PaginatedResponse<OrgUserGroupPayload>> | PaginatedResponse<OrgUserGroupPayload> | OrgUserGroupPayload[]>(API_PATHS.orgUsers.groups, {
      params: {
        service_instance_id: options.serviceInstanceId || undefined,
        service_variant: options.serviceVariant || undefined,
        page_size: 500,
      },
    })
    return normalizeUserGroups(response.data).map(mapUserGroup)
  },

  getUser: async (userId: string | number, options: { serviceInstanceId?: string | number | null; userGroupId?: string | number | null; skipAuthRedirectOn401?: boolean } = {}): Promise<OrgUser> => {
    const response = await api.get<ApiResponse<OrgUser> | OrgUser>(API_PATHS.orgUsers.detail, {
      params: {
        user_id: userId,
        user_group_id: options.userGroupId || undefined,
        service_instance_id: options.serviceInstanceId || undefined,
      },
      skipAuthRedirectOn401: options.skipAuthRedirectOn401,
    } as any)
    const payload = 'data' in response.data ? response.data.data : response.data
    return mapUser(payload as OrgUser)
  },

  listPublicGroups: async (serviceInstanceId: string | number): Promise<OrgUserGroup[]> => {
    const response = await api.get<ApiResponse<PaginatedResponse<OrgUserGroupPayload>> | PaginatedResponse<OrgUserGroupPayload> | OrgUserGroupPayload[]>(API_PATHS.orgUsers.groupsPublic, {
      params: { service_instance_id: serviceInstanceId },
    })
    return normalizeUserGroups(response.data).map(mapUserGroup)
  },

  createGroup: async (payload: OrgUserGroupUpsertPayload): Promise<OrgUserGroup> => {
    const response = await api.post<ApiResponse<OrgUserGroupPayload> | OrgUserGroupPayload>(
      API_PATHS.orgUsers.groupCreate,
      {
        title: payload.title,
        service_instance_id: payload.serviceInstanceId,
        service_variant: payload.serviceVariant,
      },
    )
    return mapUserGroup(unwrapApiData(response.data))
  },

  updateGroup: async (id: string | number, payload: { userNativity?: string; showInSidebar?: boolean }): Promise<OrgUserGroup> => {
    const body: Record<string, unknown> = {}
    if (payload.userNativity !== undefined) body.user_nativity = payload.userNativity
    if (payload.showInSidebar !== undefined) body.show_in_sidebar = payload.showInSidebar
    const response = await api.patch<ApiResponse<OrgUserGroupPayload> | OrgUserGroupPayload>(
      API_PATHS.orgUsers.groupUpdate(id),
      body,
    )
    return mapUserGroup(unwrapApiData(response.data))
  },

  grantGroups: async (payload: { targetServiceInstanceId: string; groupIds: (string | number)[] }): Promise<OrgUserGroup[]> => {
    const response = await api.post<ApiResponse<OrgUserGroupPayload[]> | OrgUserGroupPayload[]>(
      API_PATHS.orgUsers.groupGrant,
      {
        target_service_instance_id: payload.targetServiceInstanceId,
        group_ids: payload.groupIds,
      },
    )
    const raw = unwrapApiData(response.data)
    return (Array.isArray(raw) ? raw : [raw]).map(mapUserGroup)
  },

  deactivateGroup: async (id: string | number): Promise<void> => {
    await api.delete(API_PATHS.orgUsers.groupDelete(id))
  },

  listSupplierGroups: async (): Promise<OrgUserGroup[]> => {
    const response = await api.get<OrgUserGroup[]>(API_PATHS.orgUsers.supplierGroups)
    return response.data
  },

  listClientGroups: async (): Promise<OrgUserGroup[]> => {
    const response = await api.get<OrgUserGroup[]>(API_PATHS.orgUsers.clientGroups)
    return response.data
  },

  listSuppliers: async (options: string | SupplierListOptions = ''): Promise<OrgUser[]> => {
    const normalizedOptions = typeof options === 'string' ? { search: options } : options
    const { search = '', includeInactive = false, serviceInstanceId } = normalizedOptions

    return withSessionRecovery(async () => {
      const response = await api.get<OrgUser[]>(API_PATHS.orgUsers.suppliers, {
        params: {
          ...(search ? { search } : {}),
          ...(includeInactive ? { include_inactive: 1 } : {}),
          ...(serviceInstanceId ? { service_instance_id: serviceInstanceId } : {}),
        },
      })

      return dedupeUsers(response.data.map(mapSupplier))
    })
  },

  getSupplier: async (id: string): Promise<OrgUser> => {
    return withSessionRecovery(async () => {
      const response = await api.get<ApiResponse<OrgUser> | OrgUser>(API_PATHS.orgUsers.supplierDetail(id))
      const payload = 'data' in response.data ? response.data.data : response.data
      return mapSupplier(payload)
    })
  },

  createSupplier: async (
    payload: SupplierPayload,
    options: { serviceInstanceId?: string | null } = {},
  ): Promise<OrgUser> => {
    const response = await api.post<ApiResponse<OrgUser>>(
      API_PATHS.orgUsers.createSupplier,
      {
        ...payload,
        ...(payload.phone ? { country_id: payload.country_id || DEFAULT_COUNTRY_ID } : {}),
      },
      {
        params: {
          service_instance_id: options.serviceInstanceId || undefined,
        },
      },
    )
    return mapSupplier(response.data.data)
  },

  updateSupplier: async (id: string, payload: Partial<SupplierPayload>): Promise<OrgUser> => {
    const response = await api.put<ApiResponse<OrgUser>>(
      API_PATHS.orgUsers.updateSupplier(id),
      {
        ...payload,
        ...(payload.phone ? { country_id: payload.country_id || DEFAULT_COUNTRY_ID } : {}),
      },
    )
    return mapSupplier(response.data.data)
  },

  deleteSupplier: async (id: string): Promise<void> => {
    await api.delete(API_PATHS.orgUsers.deleteSupplier(id))
  },

  listAddresses: async (userId: string, options?: { includeInactive?: boolean; skipAuthRedirectOn401?: boolean }): Promise<OrgUserAddress[]> => {
    const response = await api.get(API_PATHS.profileAddress.list, {
      params: {
        user_id: userId,
        ...(options?.includeInactive ? { active: 'all' } : {}),
      },
      skipAuthRedirectOn401: options?.skipAuthRedirectOn401,
    } as any)

    return normalizeAddressList(response.data)
  },

  createAddress: async (userId: string, payload: OrgUserAddressPayload) => {
    const response = await api.post<ApiResponse<OrgUserAddress>>(
      API_PATHS.profileAddress.create,
      payload,
      {
        params: {
          user_id: userId,
        },
      },
    )

    return mapAddress(response.data.data)
  },

  updateAddress: async (addressId: string | number, payload: OrgUserAddressPayload) => {
    const response = await api.patch<ApiResponse<OrgUserAddress>>(
      API_PATHS.profileAddress.update(addressId),
      payload,
    )

    return mapAddress(response.data.data)
  },

  deleteAddress: async (addressId: string | number) => {
    await api.delete(API_PATHS.profileAddress.delete(addressId))
  },

  setDefaultAddress: async (userId: string, addressId: string | number) => {
    const response = await api.post<ApiResponse<OrgUserAddress>>(
      API_PATHS.profileAddress.setDefault(addressId),
      null,
      {
        params: {
          user_id: userId,
        },
      },
    )

    return mapAddress(response.data.data)
  },

  listClients: async (options: string | SupplierListOptions = ''): Promise<OrgUser[]> => {
    const normalizedOptions = typeof options === 'string' ? { search: options } : options
    const { search = '', includeInactive = false, serviceInstanceId } = normalizedOptions

    return withSessionRecovery(async () => {
      const response = await api.get<OrgUser[]>(API_PATHS.orgUsers.clients, {
        params: {
          ...(search ? { search } : {}),
          ...(includeInactive ? { include_inactive: 1 } : {}),
          ...(serviceInstanceId ? { service_instance_id: serviceInstanceId } : {}),
        },
      })

      return dedupeUsers(response.data.map(mapClient))
    })
  },

  getClient: async (id: string): Promise<OrgUser> => {
    return withSessionRecovery(async () => {
      const response = await api.get<ApiResponse<OrgUser> | OrgUser>(API_PATHS.orgUsers.clientDetail(id))
      const payload = 'data' in response.data ? response.data.data : response.data
      return mapClient(payload)
    })
  },

  createClient: async (
    payload: ClientPayload,
    options: { serviceInstanceId?: string | number | null } = {},
  ): Promise<OrgUser> => {
    const response = await api.post<ApiResponse<OrgUser>>(
      API_PATHS.orgUsers.createClient,
      {
        ...payload,
        ...(payload.phone ? { country_id: payload.country_id || DEFAULT_COUNTRY_ID } : {}),
      },
      {
        params: {
          service_instance_id: options.serviceInstanceId || undefined,
        },
      },
    )
    return mapClient(response.data.data)
  },

  updateClient: async (id: string, payload: Partial<ClientPayload>): Promise<OrgUser> => {
    const response = await api.put<ApiResponse<OrgUser>>(
      API_PATHS.orgUsers.updateClient(id),
      {
        ...payload,
        ...(payload.phone ? { country_id: payload.country_id || DEFAULT_COUNTRY_ID } : {}),
      },
    )
    return mapClient(response.data.data)
  },

  deleteClient: async (id: string): Promise<void> => {
    await api.delete(API_PATHS.orgUsers.deleteClient(id))
  },
}